import { Flex, Table, Thead, Tbody, Tr, Th, Text } from '@chakra-ui/react';
import React from 'react';

import { useDraggableRow } from '../hooks';
import * as types from '../types';

export const TableDraggable = <T extends Object>({
  data,
  tableHeader,
  comparisonKey,
  children,
}: types.TableDraggable.ParentProps<T> & {
  children: (props: {
    rows: T[];
    moveRow: types.MoveRow;
    isOrderLocked: boolean;
  }) => React.ReactNode;
}) => {
  const {
    useRowState,
    useOrderLockedState,
    moveRow,
    ui: { LockOrderButton, RestoreOrderButton, SaveOrderCallout },
  } = useDraggableRow<T>({ comparisonKey });
  const [rows] = useRowState(data);
  const [isOrderLocked, setIsOrderLocked] = useOrderLockedState();

  return (
    <>
      <Flex alignItems="center">
        <LockOrderButton
          onSaveOrder={() =>
            setIsOrderLocked((prevIsOrderLocked) => {
              const newOrderIsLocked = !prevIsOrderLocked;
              // typically you'd update parent state/ context, or in the case of CA the formProps
              return newOrderIsLocked;
            })
          }
        />
        <Text fontWeight="bold" fontSize="20px">
          {tableHeader}
        </Text>
        <RestoreOrderButton />
      </Flex>
      <SaveOrderCallout message="Unlocked changes to the Good Burger Employee List will be lost" />
      <Table>{children({ rows, moveRow, isOrderLocked })}</Table>
    </>
  );
};

type HeadersForDraggableHandleWithPanelProps = {
  isOrderLocked: boolean;
  children: React.ReactNode;
};
const HeadersForDraggableHandleWithPanel = ({
  isOrderLocked,
  children,
}: HeadersForDraggableHandleWithPanelProps) => {
  return (
    <Thead>
      <Tr>
        <Th display={isOrderLocked ? 'none' : undefined} />
        {children}
        <Th display={!isOrderLocked ? 'none' : undefined} />
      </Tr>
    </Thead>
  );
};
HeadersForDraggableHandleWithPanel.displayName =
  'TableDraggable.HeadersForDraggableHandleWithPanel';
TableDraggable.HeadersForDraggableHandleWithPanel = HeadersForDraggableHandleWithPanel;

type HeadersForDraggableHandleProps = {
  isOrderLocked: boolean;
  children: React.ReactNode;
};
const HeadersForDraggableHandle = ({
  isOrderLocked,
  children,
}: HeadersForDraggableHandleProps) => {
  return (
    <Thead>
      <Tr>
        <Th display={isOrderLocked ? 'none' : undefined} />
        {children}
      </Tr>
    </Thead>
  );
};
HeadersForDraggableHandle.displayName =
  'TableDraggable.HeadersForDraggableHandle';
TableDraggable.HeadersForDraggableHandle = HeadersForDraggableHandle;

type HeadersForDraggableWithPanelProps = {
  isOrderLocked: boolean;
  children: React.ReactNode;
};
const HeadersForDraggableWithPanel = ({
  isOrderLocked,
  children,
}: HeadersForDraggableWithPanelProps) => {
  return (
    <Thead>
      <Tr>
        {children}
        <Th display={isOrderLocked ? 'none' : undefined} />
      </Tr>
    </Thead>
  );
};
HeadersForDraggableWithPanel.displayName =
  'TableDraggable.HeadersForDraggableWithPanel';
TableDraggable.HeadersForDraggableWithPanel = HeadersForDraggableWithPanel;

const Headers = ({ children }: { children: React.ReactNode }) => {
  return (
    <Thead>
      <Tr>{children}</Tr>
    </Thead>
  );
};
Headers.displayName = 'TableDraggable.Headers';
TableDraggable.Headers = Headers;

const Rows = ({ children }: { children: React.ReactNode }) => {
  return <Tbody>{children}</Tbody>;
};
Rows.displayName = 'TableDraggableWithPanels.Rows';
TableDraggable.Rows = Rows;
